import type { CurvePoint, Rehearsal } from './types';

// Computes the ambiguity tolerance curve points from rehearsals.
// Primary y is tolerance_after. We also surface residue reduction and success for context.
// No "progress score", no normative judgment. Just the trace of what the person has rehearsed.

export function computeCurve(rehearsals: Rehearsal[]): CurvePoint[] {
  if (!rehearsals || rehearsals.length === 0) return [];

  return [...rehearsals]
    .sort((a, b) => a.created_at - b.created_at)
    .map((r) => {
      const beforeAvg =
        (r.emotional_residue_before.shame +
          r.emotional_residue_before.anxiety +
          r.emotional_residue_before.fatigue +
          r.emotional_residue_before.self_doubt) / 4;
      const afterAvg =
        (r.emotional_residue_after.shame +
          r.emotional_residue_after.anxiety +
          r.emotional_residue_after.fatigue +
          r.emotional_residue_after.self_doubt) / 4;

      return {
        rehearsal_id: r.id,
        created_at: r.created_at,
        tolerance_before: r.tolerance_before,
        tolerance_after: r.tolerance_after,
        reappraisal_success: r.reappraisal_success,
        rumination_theme: r.rumination_theme,
        outcome_type: r.outcome_type,
        residue_reduction: Math.max(0, Math.round((beforeAvg - afterAvg) * 10) / 10),
      };
    });
}

export function getCurveSummary(points: CurvePoint[]) {
  if (points.length === 0) {
    return {
      count: 0,
      avgToleranceAfter: null,
      avgSuccess: null,
      avgResidueDrop: null,
      direction: 'flat' as const,
    };
  }

  const last = points[points.length - 1];
  const first = points[0];

  const avgToleranceAfter =
    Math.round((points.reduce((s, p) => s + p.tolerance_after, 0) / points.length) * 10) / 10;

  const avgSuccess =
    Math.round((points.reduce((s, p) => s + p.reappraisal_success, 0) / points.length) * 10) / 10;

  const avgResidueDrop =
    Math.round((points.reduce((s, p) => s + p.residue_reduction, 0) / points.length) * 10) / 10;

  const direction =
    last.tolerance_after > first.tolerance_after + 0.5
      ? 'rising'
      : last.tolerance_after < first.tolerance_after - 0.5
      ? 'falling'
      : 'flat';

  return {
    count: points.length,
    avgToleranceAfter,
    avgSuccess,
    avgResidueDrop,
    direction,
  };
}

// Descriptive, non-gamified language only.
export function describeCurve(points: CurvePoint[]): string {
  if (points.length === 0) return 'No rehearsals yet. Your curve will appear after your first saved rehearsal.';
  if (points.length === 1) return 'One rehearsal recorded. The curve will become more useful with a few more private sittings over time.';
  const s = getCurveSummary(points);
  const dirText =
    s.direction === 'rising'
      ? 'tolerance after rehearsal has been gently rising'
      : s.direction === 'falling'
      ? 'tolerance after rehearsal has been lower recently'
      : 'tolerance has been relatively steady';
  return `${s.count} rehearsals. Average post-rehearsal tolerance ${s.avgToleranceAfter}. On average the emotional charge dropped ${s.avgResidueDrop} points. ${dirText}.`;
}
